import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import { PACKAGE_NAME, WIDGET_SERVICE_API_BASE_URL, TEAM_NAME } from '../../common/constants';
import type { ConfigType, WidgetSettingsObject } from '../../common/types';
import type { SaveWidgetSettingsResponse, ReturnObject } from './types';

export const postWidgetSettings = async (
	projectId: number,
	widgetSettings: ConfigType,
): Promise<SaveWidgetSettingsResponse> =>
	performPostRequest(
		`
    ${WIDGET_SERVICE_API_BASE_URL}/config?projectId=${projectId}`,
		{
			body: JSON.stringify(widgetSettings),
		},
	).catch((error: Error) => {
		fireErrorAnalytics({
			meta: {
				id: 'postWidgetSettings',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	});

export const putWidgetSettings = async (
	projectId: number,
	widgetSettings: WidgetSettingsObject,
): Promise<SaveWidgetSettingsResponse> =>
	performPutRequest(
		`
        ${WIDGET_SERVICE_API_BASE_URL}/config?projectId=${projectId}`,
		{
			body: JSON.stringify(widgetSettings),
		},
	).catch((error: Error) => {
		fireErrorAnalytics({
			meta: {
				id: 'putWidgetSettings',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	});

export const useSaveWidgetSettings = (projectId: number, projectKey: string): ReturnObject => {
	const [{ data: serviceDeskContext }] = useServiceDeskContext(projectKey);
	const { serviceDeskId } = serviceDeskContext || {};
	const request = useCallback(
		(widgetSettings: ConfigType | WidgetSettingsObject) => {
			if (serviceDeskId !== undefined) {
				if ('key' in widgetSettings) {
					return putWidgetSettings(projectId, {
						...widgetSettings,
						configuration: { ...widgetSettings.configuration, serviceDeskId },
					});
				}
				return postWidgetSettings(projectId, { ...widgetSettings, serviceDeskId });
			}
			return Promise.resolve(undefined);
		},
		[projectId, serviceDeskId],
	);
	const { loading, error, data, fetch } = useService<
		SaveWidgetSettingsResponse,
		ConfigType | WidgetSettingsObject
	>(request);

	return [{ loading, error, data }, fetch];
};
